// src/utils/dateUtils.ts

/**
 * Converts a date string from DD/MM/YYYY or YYYY/MM/DD to YYYY-MM-DD format.
 * If the date is already in YYYY-MM-DD format, it returns it as is.
 * Returns an empty string if the date is invalid or empty.
 * @param dateString The date string to format.
 * @returns The formatted date string or an empty string.
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return '';
  }

  // Check if the date is in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Check if the date is in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }

  // Check if the date is in YYYY/MM/DD format
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('/');
    return `${year}-${month}-${day}`;
  }

  // Return empty string for any other invalid format
  return '';
};

// Month map for parsing date strings
const monthMap: { [key: string]: number } = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/**
 * Parses a submission deadline string into a Date object.
 * Handles various formats including:
 * - DD/Mon/YYYY HH:MM AM/PM (with optional UTC offset)
 * - ISO 8601
 * @param deadlineStr The deadline string to parse.
 * @returns A Date object or null if parsing fails.
 */
export const parseSubmissionDeadline = (deadlineStr: string | null | undefined): Date | null => {
  if (!deadlineStr || typeof deadlineStr !== 'string') {
    return null;
  }

  // Try standard Date constructor first for ISO strings
  const simpleDate = new Date(deadlineStr);
  if (!Number.isNaN(simpleDate.getTime()) && deadlineStr.includes('-')) {
    return simpleDate;
  }

  const dateTimeRegex =
    /(\d{1,2})\/([A-Za-z]{3})\/(\d{4})\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*(?:\(UTC([+-]\d{2})\))?/;
  const dateTimeMatch = deadlineStr.match(dateTimeRegex);

  if (dateTimeMatch) {
    const [, dayStr, monthStr, yearStr, hourStr, minuteStr, ampm, utcOffset] = dateTimeMatch;
    const month = monthMap[monthStr as keyof typeof monthMap];

    if (month !== undefined) {
      let hour = parseInt(hourStr || '0', 10);
      if (ampm && ampm.toUpperCase() === 'PM' && hour !== 12) {
        hour += 12;
      } else if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) {
        hour = 0;
      }

      // Create the base date string in ISO format to avoid browser parsing inconsistencies
      // YYYY-MM-DDTHH:mm:ss
      const isoBase = `${yearStr}-${String(month + 1).padStart(2, '0')}-${String(dayStr).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minuteStr}:00`;

      if (utcOffset) {
        // Append offset: +HH:00
        const dateObj = new Date(`${isoBase}${utcOffset}:00`);
        if (!Number.isNaN(dateObj.getTime())) {
          return dateObj;
        }
      } else {
        // Local time
        const dateObj = new Date(isoBase);
        if (!Number.isNaN(dateObj.getTime())) {
          return dateObj;
        }
      }
    }
  }

  return null;
};

/**
 * Calculates the number of days remaining until the deadline.
 * @param deadlineInput The deadline as a string, Date, or number (timestamp).
 * @returns The number of days (positive for future, negative for past), or null if invalid.
 */
export const calculateDaysRemaining = (
  deadlineInput: string | number | Date | null | undefined
): number | null => {
  if (!deadlineInput) {
    return null;
  }

  let deadlineDate: Date | null = null;

  if (deadlineInput instanceof Date) {
    deadlineDate = deadlineInput;
  } else if (typeof deadlineInput === 'number') {
    deadlineDate = new Date(deadlineInput * 1000); // Assume seconds
  } else if (typeof deadlineInput === 'string') {
    deadlineDate = parseSubmissionDeadline(deadlineInput);
    // Fallback if custom parser fails
    if (!deadlineDate) {
      const d = new Date(deadlineInput);
      if (!Number.isNaN(d.getTime())) {
        deadlineDate = d;
      }
    }
  }

  if (!deadlineDate || Number.isNaN(deadlineDate.getTime())) {
    return null;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(
    deadlineDate.getFullYear(),
    deadlineDate.getMonth(),
    deadlineDate.getDate()
  );

  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};
